import type { ToolActionServerStatus } from "./useToolActions";

const isTerminal = (s: ToolActionServerStatus | undefined): boolean =>
  s === "executed" || s === "rejected" || s === "failed" || s === "blocked";

/**
 * Decides when a chat should continue on its own after the user approved
 * actions in this session. Only approvals recorded through `track` count, so a
 * card that loads already executed, or one somebody else approved, never
 * continues the chat.
 *
 * A batch settles once every tracked action is terminal: all executed fires one
 * continuation; any failure drops the batch without one, since the card already
 * shows the failure and the user decides what to do next. A settled batch is
 * forgotten, so each approval continues at most once.
 *
 * A turn still streaming when the batch settles also drops it: the turn that
 * proposed the action waits for the approval server-side and carries on with
 * the result itself, so a client "continue" behind it would be a duplicate.
 * The continuation only covers approvals that execute after the turn gave up
 * waiting, or ones the user came back to later.
 */
export const createApprovalTracker = () => {
  const approved = new Map<string, Set<string>>();

  return {
    track: (chatId: string, actionId: string): void => {
      const ids = approved.get(chatId) ?? new Set<string>();
      ids.add(actionId);
      approved.set(chatId, ids);
    },
    // Right after approval the cached status still reads "pending" until the
    // first poll lands, so anything non-terminal is in flight.
    hasInFlight: (chatId: string, statusOf: (actionId: string) => ToolActionServerStatus | undefined): boolean => {
      for (const id of approved.get(chatId) ?? []) {
        if (!isTerminal(statusOf(id))) return true;
      }
      return false;
    },
    settle: (
      chatId: string,
      statusOf: (actionId: string) => ToolActionServerStatus | undefined,
      streaming: boolean
    ): "continue" | null => {
      const ids = approved.get(chatId);
      if (!ids || ids.size === 0) return null;
      let allExecuted = true;
      for (const id of ids) {
        const status = statusOf(id);
        if (!isTerminal(status)) return null;
        if (status !== "executed") allExecuted = false;
      }
      approved.delete(chatId);
      return !streaming && allExecuted ? "continue" : null;
    },
  };
};
